'use client';

import { useEffect, useLayoutEffect, useState } from 'react';
import type { RefObject } from 'react';

/**
 * Выход слоя ходом назад: меню, закрытое по выбору, тапу мимо или Escape, не исчезает в тот
 * же кадр, а доигрывает обратный ход (роль «меню» — --m-dur-fast×1.2 по --m-ease), и только
 * потом снимается с разметки. Тот же приём, что у Radix Presence для KDialog: пока идёт выход,
 * узел стоит с `data-state="closed"`, CSS вешает на него кадры ухода, конец кадров
 * (`animationend`) снимает узел.
 *
 * INVARIANT(scriptera): длительность ухода хук НЕ знает и не заводит — её задаёт CSS роли
 * (`[data-state='closed']`). Анимации у узла нет (`animation-name: none`: вид без кадров ухода,
 * скрытый узел) — снимаем сразу, без таймеров. Причина: числа движения живут только в
 * tokens.css (Закон 5 ТЗ WEB-LOOK), свой таймер разошёлся бы с ролью. Канон:
 * ~/projects/ANIM-FIX-TZ.md О2, решение владельца 04.10.2026 «закрытие меню с анимацией — да».
 */
export function useExitPresence(
  open: boolean,
  ref: RefObject<HTMLElement | null>,
): { mounted: boolean; state: 'open' | 'closed' } {
  const [shown, setShown] = useState(open);
  useEffect(() => {
    if (open) setShown(true);
  }, [open]);
  useLayoutEffect(() => {
    if (open || !shown) return;
    const el = ref.current;
    const cs = el ? getComputedStyle(el) : null;
    if (!el || !cs || cs.animationName === 'none' || cs.display === 'none') {
      setShown(false);
      return;
    }
    const done = (e: AnimationEvent): void => {
      if (e.target === el) setShown(false);
    };
    el.addEventListener('animationend', done);
    el.addEventListener('animationcancel', done);
    return () => {
      el.removeEventListener('animationend', done);
      el.removeEventListener('animationcancel', done);
    };
  }, [open, shown, ref]);
  return { mounted: open || shown, state: open ? 'open' : 'closed' };
}
